/**
 * Frontend logger — thin wrapper over Application Insights with PII scrubbing.
 *
 * In production all logs flow to App Insights (via `trackTrace` /
 * `trackException`). In dev we also mirror to the console for visibility.
 *
 * `scrub()` strips the most common PII categories from free-text messages
 * before they leave the browser:
 *   - email addresses
 *   - bearer tokens / JWT-looking strings
 *   - 10+ digit numeric sequences (phone, SSN, card PAN)
 */

import { appInsights } from './appInsights';

const EMAIL_RE  = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const JWT_RE    = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_RE = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;
const LONGNUM   = /\b\d{10,}\b/g;

export function scrub(input: unknown): string {
  if (input == null) return '';
  let text: string;
  try {
    text = typeof input === 'string' ? input : JSON.stringify(input);
  } catch {
    text = String(input);
  }
  return text
    .replace(JWT_RE,    '<redacted:jwt>')
    .replace(BEARER_RE, 'Bearer <redacted>')
    .replace(EMAIL_RE,  '<redacted:email>')
    .replace(LONGNUM,   '<redacted:number>');
}

const isDev = import.meta.env?.DEV === true;

export const logger = {
  info(message: string, properties?: Record<string, unknown>): void {
    const scrubbed = scrub(message);
    try {
      appInsights.trackTrace({ message: scrubbed, severityLevel: 1 }, sanitize(properties));
    } catch { /* noop */ }
    if (isDev) console.info(scrubbed);
  },
  warn(message: string, properties?: Record<string, unknown>): void {
    const scrubbed = scrub(message);
    try {
      appInsights.trackTrace({ message: scrubbed, severityLevel: 2 }, sanitize(properties));
    } catch { /* noop */ }
    if (isDev) console.warn(scrubbed);
  },
  error(message: string, error?: unknown, properties?: Record<string, unknown>): void {
    const scrubbed = scrub(message);
    try {
      if (error instanceof Error) {
        appInsights.trackException({ exception: error }, {
          message: scrubbed,
          ...sanitize(properties),
        });
      } else {
        appInsights.trackTrace({ message: scrubbed, severityLevel: 3 }, sanitize(properties));
      }
    } catch { /* noop */ }
    if (isDev) console.error(scrubbed, error);
  },
};

function sanitize(props?: Record<string, unknown>): Record<string, string> | undefined {
  if (!props) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) {
    out[k] = scrub(v);
  }
  return out;
}
